import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface VoiceAgentSession {
  id: string;
  session_id: string;
  user_id: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  status: string;
  metadata: Record<string, unknown>;
}

export interface VoiceAgentMessage {
  id: string;
  session_id: string;
  role: string;
  content: string;
  timestamp: string;
  metadata: Record<string, unknown>;
}

export interface VoiceAgentDailyStats {
  id: string;
  date: string;
  total_sessions: number;
  total_messages: number;
  avg_duration_seconds: number | null;
  unique_users: number;
}

// Helper to execute raw queries since tables may not be in generated types yet
const executeQuery = async <T>(query: string): Promise<T[]> => {
  const { data, error } = await supabase.rpc('execute_sql' as never, { sql: query } as never);
  if (error) {
    // If RPC doesn't exist, tables likely don't exist yet - return empty
    console.warn("Query failed, tables may not exist:", error.message);
    return [];
  }
  return (data || []) as T[];
};

export const useVoiceAgentSessions = (limit = 50) => {
  return useQuery({
    queryKey: ["voice-agent-sessions", limit],
    queryFn: async () => {
      try {
        // Use direct fetch to bypass type checking for new tables
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/voice_agent_sessions?order=started_at.desc&limit=${limit}`,
          {
            headers: {
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
          }
        );
        
        if (!response.ok) {
          console.warn("Voice agent sessions table may not exist yet");
          return [];
        }
        
        return (await response.json()) as VoiceAgentSession[];
      } catch (error) {
        console.warn("Failed to fetch sessions:", error);
        return [];
      }
    },
  });
};

export const useVoiceAgentMessages = (sessionId: string | null) => {
  return useQuery({
    queryKey: ["voice-agent-messages", sessionId],
    queryFn: async () => {
      if (!sessionId) return [];
      
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/voice_agent_messages?session_id=eq.${sessionId}&order=timestamp.asc`,
          {
            headers: {
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
          }
        );
        
        if (!response.ok) return [];
        return (await response.json()) as VoiceAgentMessage[];
      } catch (error) {
        return [];
      }
    },
    enabled: !!sessionId,
  });
};

export const useVoiceAgentDailyStats = (days = 30) => {
  return useQuery({
    queryKey: ["voice-agent-daily-stats", days],
    queryFn: async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const dateStr = startDate.toISOString().split("T")[0];

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/voice_agent_daily_stats?date=gte.${dateStr}&order=date.asc`,
          {
            headers: {
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
          }
        );
        
        if (!response.ok) return [];
        return (await response.json()) as VoiceAgentDailyStats[];
      } catch (error) {
        return [];
      }
    },
  });
};

export const useVoiceAgentOverview = () => {
  return useQuery({
    queryKey: ["voice-agent-overview"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const token = (await supabase.auth.getSession()).data.session?.access_token || apiKey;
      
      const headers = {
        apikey: apiKey,
        Authorization: `Bearer ${token}`,
        Prefer: "count=exact",
      };

      try {
        // Get total sessions count
        const totalRes = await fetch(
          `${baseUrl}/rest/v1/voice_agent_sessions?select=id`,
          { headers: { ...headers, Prefer: "count=exact" } }
        );
        const totalSessions = totalRes.ok ? parseInt(totalRes.headers.get("content-range")?.split("/")[1] || "0") : 0;

        // Get today's sessions
        const today = new Date().toISOString().split("T")[0];
        const todayRes = await fetch(
          `${baseUrl}/rest/v1/voice_agent_sessions?started_at=gte.${today}&select=id`,
          { headers: { ...headers, Prefer: "count=exact" } }
        );
        const todaySessions = todayRes.ok ? parseInt(todayRes.headers.get("content-range")?.split("/")[1] || "0") : 0;

        // Get active sessions
        const activeRes = await fetch(
          `${baseUrl}/rest/v1/voice_agent_sessions?status=eq.active&select=id`,
          { headers: { ...headers, Prefer: "count=exact" } }
        );
        const activeSessions = activeRes.ok ? parseInt(activeRes.headers.get("content-range")?.split("/")[1] || "0") : 0;

        // Get average duration from completed sessions
        const durationRes = await fetch(
          `${baseUrl}/rest/v1/voice_agent_sessions?duration_seconds=not.is.null&select=duration_seconds`,
          { headers }
        );
        const durationData = durationRes.ok ? await durationRes.json() : [];
        const avgDuration = durationData.length
          ? durationData.reduce((sum: number, s: { duration_seconds: number }) => sum + (s.duration_seconds || 0), 0) / durationData.length
          : 0;

        return {
          totalSessions,
          todaySessions,
          activeSessions,
          avgDuration: Math.round(avgDuration),
        };
      } catch (error) {
        console.warn("Failed to fetch overview:", error);
        return {
          totalSessions: 0,
          todaySessions: 0,
          activeSessions: 0,
          avgDuration: 0,
        };
      }
    },
  });
};
